import { site } from '@shared/site.config';
import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import { faqPageJsonLd } from '@/lib/seo';
import LocalPageSheet from '@/components/LocalPageSheet';
import type { Faq } from '@/components/LocalPageSheet';

export const meta = {
  path: '/retail-space-for-lease-uptown-waterloo-downtown-kitchener',
  title: 'Retail Space for Lease — Uptown Waterloo & Downtown Kitchener',
  description:
    'Retail leasing in Uptown Waterloo and Downtown Kitchener, along the ION line and the ongoing core redevelopment. Direct line to Pinnacle Commercial, no listings portal.',
};

const kitchener = site.serviceAreas.find((a) => a.slug === 'kitchener')!;
const waterloo = site.serviceAreas.find((a) => a.slug === 'waterloo')!;

const FAQS: Faq[] = [
  {
    q: 'What kind of retail does well in Uptown Waterloo vs. Downtown Kitchener?',
    a: "They draw differently — Uptown Waterloo pulls from the university-adjacent population and a denser residential base right around the core, while Downtown Kitchener's mix is shifting alongside its ongoing redevelopment. Which one — or both — fits depends on your concept and format, worth talking through directly.",
  },
  {
    q: 'Is there a current list of available retail units?',
    a: 'No public listings page. Ground-floor retail in both cores turns over unpredictably, and the units worth knowing about often move before they\'re ever formally advertised — a direct conversation gets you ahead of that.',
  },
  {
    q: 'Does foot traffic near the ION line actually matter for retail?',
    a: "It's one factor among several — frontage, unit depth, visibility and the immediate block matter just as much. I'll give you a straight read on a specific address rather than a generic answer about the line itself.",
  },
  {
    q: 'Can you help with short-term or pop-up retail terms, not just long leases?',
    a: "Yes, where a landlord is open to it — those terms vary block by block and aren't the kind of thing that shows up on a public list.",
  },
];

export default function RetailUptownDowntown() {
  useTitle(meta.title, meta.description);
  useJsonLd('faq-retail-uptown-downtown', faqPageJsonLd(FAQS));

  return (
    <LocalPageSheet
      sheetNo="S-05"
      kicker="Local search — retail leasing"
      h1="Retail space for lease, Uptown Waterloo & Downtown Kitchener."
      intro="Ground-floor and street-retail representation across the two cores — Uptown Waterloo's university-adjacent draw and Downtown Kitchener's redevelopment corridor — covered as one connected market."
      glyph="retail"
      audiences={[
        {
          label: 'Looking for space',
          body: "Tell me your concept, format and what you need from frontage and foot traffic, and I'll tell you what's realistic in Uptown Waterloo and Downtown Kitchener right now.",
          ctaLabel: 'Tell me what you need',
        },
        {
          label: 'Have space to lease',
          body: 'Positioning and tenant sourcing for ground-floor retail in either core, direct to qualified operators rather than a blanket listing.',
          ctaLabel: 'Talk through your space',
        },
      ]}
      localLabel="Uptown Waterloo & Downtown Kitchener, retail"
      localBody={
        <>
          <p>{waterloo.blurb}</p>
          <p>{kitchener.blurb}</p>
        </>
      }
      honestLabel="No listings page, on purpose"
      honestBody="Retail units in both cores move fast and often change hands before they're ever formally listed anywhere. Reach out with what you're looking for and you'll get the real current picture, not a stale page."
      faqs={FAQS}
      interest="leasing"
      closingLine="Retail space in Uptown Waterloo and Downtown Kitchener — one conversation, both cores."
      related={[
        { to: '/leasing', label: 'Full leasing services' },
        { to: '/office-space-for-lease-kitchener-waterloo', label: 'Office — Kitchener & Waterloo' },
        { to: '/industrial-space-for-lease-cambridge', label: 'Industrial — Cambridge' },
      ]}
    />
  );
}
